import React, { useEffect, useState } from 'react'
import { Menu, MenuItem, Position, Checkbox } from '@blueprintjs/core'
import { Select2 } from '@blueprintjs/select'
import { Button } from '..'
import { IUIButtonProps } from '../buttons/Button'
import fuzzy from 'fuzzaldrin-plus'
import { Flex, HighlightedText, IconButton } from '@ui'
import { debounce } from 'lodash'
import api, { IHRLocation } from '@api'

interface HRLocationSelectProps {
  value: IHRLocation | null
  onChange: (location: IHRLocation) => void
  buttonProps?: IUIButtonProps
  btnText?: React.ReactNode
  itemDisabled?: (item: IHRLocation) => boolean
  multi?: boolean
  clearable?: boolean
  searchBoxPlaceholder?: string
}

const HRLocationSelect = Select2<IHRLocation>
const maxItemsQuantity = 25

export default function HRLocationSelectWrapper({
  value,
  onChange,
  buttonProps,
  btnText,
  itemDisabled,
  multi,
  clearable = false,
  searchBoxPlaceholder = 'Filter...',
}: HRLocationSelectProps) {
  const locations = useHRLocations()
  const [query, setQuery] = useState('')
  const [items, setItems] = useState(
    locations.length > maxItemsQuantity ? locations.slice(0, maxItemsQuantity) : locations
  )

  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 25
  const totalPages = Math.ceil(locations.length / itemsPerPage)

  const setItemsByPage = () => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    setItems(locations.slice(startIndex, endIndex))
  }

  const goToNextPage = () => {
    setCurrentPage((prevPage) => prevPage + 1)
  }

  const goToPreviousPage = () => {
    setCurrentPage((prevPage) => prevPage - 1)
  }

  useEffect(() => {
    setItemsByPage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, currentPage])

  const search = debounce((query) => {
    setItems(
      query
        ? fuzzy.filter(locations, query, { key: 'location' }).slice(0, maxItemsQuantity)
        : locations.slice(0, maxItemsQuantity)
    )
  }, 0)

  return (
    <>
      <HRLocationSelect
        inputProps={{ placeholder: searchBoxPlaceholder }}
        query={query}
        onQueryChange={(newQuery) => {
          setQuery(newQuery)
          search(newQuery)
        }}
        resetOnSelect
        popoverProps={{
          position: Position.BOTTOM,
          minimal: true,
        }}
        items={items.sort(({ location: loc1 }, { location: loc2 }) =>
          loc1 < loc2 ? -1 : loc1 > loc2 ? 1 : 0
        )}
        noResults={<MenuItem disabled text="No results" />}
        itemListRenderer={({ items, renderItem }) => {
          const startIndex = (currentPage - 1) * itemsPerPage + 1
          const endIndex = (currentPage - 1) * itemsPerPage + items.length
          return (
            <>
              {
                <div className="text-xs text-grey-700 mt-1">
                  {locations.length > maxItemsQuantity
                    ? query.length
                      ? `${items.length} results`
                      : `Showing ${startIndex} to ${endIndex} of ${locations.length} locations`
                    : `Showing all ${items.length} locations`}
                  {
                    <Flex className="pagination-buttons" justify="space-between">
                      {
                        <button
                          className="pagination-button"
                          onClick={goToPreviousPage}
                          style={{
                            fontWeight: 'bold',
                            textDecoration: 'underline',
                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                          }}
                          disabled={currentPage === 1}>
                          Previous
                        </button>
                      }
                      {
                        <button
                          className="pagination-button"
                          onClick={goToNextPage}
                          style={{
                            fontWeight: 'bold',
                            textDecoration: 'underline',
                            cursor: endIndex === locations.length ? 'not-allowed' : 'pointer',
                          }}
                          disabled={endIndex === locations.length}>
                          Next
                        </button>
                      }
                    </Flex>
                  }
                </div>
              }
              <Menu style={{ minWidth: 300 }}>{items.map((item, i) => renderItem(item, i))}</Menu>
            </>
          )
        }}
        itemRenderer={(location, { handleClick, modifiers, query }) => {
          return modifiers.matchesPredicate ? (
            <MenuItem
              active={modifiers.active}
              key={location.id}
              data-item={location.location}
              shouldDismissPopover={false}
              onClick={(e: React.MouseEvent<HTMLElement>) => {
                if (multi) {
                  e.preventDefault()
                  onChange(location)
                  return
                }
                handleClick(e)
              }}
              text={
                <Flex align="center">
                  {multi && <Checkbox checked={itemDisabled?.(location)} />}
                  <HighlightedText text={location.location} query={query} />
                </Flex>
              }
            />
          ) : null
        }}
        onItemSelect={onChange}>
        <Button
          {...buttonProps}
          data-button="location_select"
          rightIconName={value ? undefined : btnText ? undefined : 'chevron-down'}
          text={value ? value.location : btnText || 'Select'}
        />
        {value && clearable && (
          <IconButton
            minimal
            iconName="times"
            tooltip="Clearsss"
            data-button="clear-button"
            onClick={() => {
              onChange(null as unknown as IHRLocation)
            }}
          />
        )}
      </HRLocationSelect>
    </>
  )
}

let persistedState: IHRLocation[] = []

export function useHRLocations(): IHRLocation[] {
  const [locations, setLocations] = useState<IHRLocation[]>(persistedState)

  useEffect(() => {
    api.company.geoLocations().then((geoLocations) => setLocations(geoLocations))
  }, [])

  useEffect(() => {
    persistedState = locations
  })

  return locations
}
